import { displayText } from '../../../terminology/store';
import { useTerminology } from '../../../terminology/TerminologyContext';
import React, { useState, useEffect, useContext } from 'react';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import ThemeContext from '../../ThemeContext';
import Pagination from '../../Utils/Pagination';
import api, { getClientAdminsByReseller } from '../../services/api';
import 'react-toastify/dist/ReactToastify.css';
import 'bootstrap-icons/font/bootstrap-icons.css';

const Reports = ({ activeSubTab = 'user' }) => {
  useTerminology();
  const { theme } = useContext(ThemeContext);
  const navigate = useNavigate();

  // Typography from theme
  const fontFamily =
    theme.typography?.fontFamily ||
    '"Roboto", "Helvetica Neue", Arial, sans-serif';
  const bodyFontSize = theme.typography?.bodyFontSize || 14;
  const titleFontSize = theme.typography?.titleFontSize || 24;

  const [reportData, setReportData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [gotoPage, setGotoPage] = useState('');
  const [sortField, setSortField] = useState('id');
  const [sortOrder, setSortOrder] = useState('asc');

  const [selectedIds, setSelectedIds] = useState(new Set());
  const [selectAll, setSelectAll] = useState(false);

  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');

const userRole = (
  currentUser?.role ||
  currentUser?.user_role ||
  ''
).toLowerCase();

const loggedInResellerId =
  currentUser?.reseller_id ||
  currentUser?.reseller?.id ||
  currentUser?.id ||
  currentUser?.user_id ||
  null;

  const today = new Date();
  const currentMonth = today.getMonth() + 1;
  const currentYear = today.getFullYear();

  const [statusFilter, setStatusFilter] = useState('all');
  const [monthFilter, setMonthFilter] = useState(String(currentMonth));
  const [yearFilter, setYearFilter] = useState(String(currentYear));

  const pad = (n) => String(n).padStart(2, '0');
  const [startDate, setStartDate] = useState(`${currentYear}-01-01`);
  const [endDate, setEndDate] = useState(
    `${currentYear}-${pad(currentMonth)}-${pad(today.getDate())}`
  );

  const reportConfig = {
    user: { title: 'User Report' },
    commission: { title: 'Commission Report' },
    usage: { title: 'Usage Report' },
  };

  const currentReport = reportConfig[activeSubTab] || reportConfig.user;

  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
  ];

  const years = Array.from({ length: currentYear - 2015 + 3 }, (_, i) => 2015 + i);

  const normalizeStatus = (s) => {
    if (!s && s !== '') return '';
    const v = String(s).trim().toLowerCase();
    if (v === 'active') return 'active';
    if (v === 'inactive' || v === 'deactivated' || v === 'disabled') return 'inactive';
    return v;
  };

  const parseToDate = (value) => {
    if (!value) return null;
    try {
      const str = String(value).trim();
      if (/^\d{4}-\d{1,2}$/.test(str)) return new Date(`${str}-01`);
      if (/^\d{4}$/.test(str)) return new Date(`${str}-01-01`);
      const d = new Date(str);
      return isNaN(d.getTime()) ? null : d;
    } catch (e) {
      return null;
    }
  };

  useEffect(() => {
  const fetchResellers = async () => {
    setLoading(true);
    try {
      let resellers = [];

      console.log('Reports currentUser:', currentUser);
      console.log('Reports userRole:', userRole);
      console.log('Reports loggedInResellerId:', loggedInResellerId);

      if (userRole === 'superadmin' || userRole === 'admin') {
        const response = await api.get('/resellers');
        resellers = response.data?.data || response.data || [];
      } else if (userRole === 'reseller') {
  const response = await api.get('/resellers');
  const allResellers = response.data?.data || response.data || [];

  const currentResellerName =
    currentUser?.reseller_name ||
    currentUser?.company_name ||
    currentUser?.company ||
    currentUser?.name ||
    '';

  const currentEmail =
    (currentUser?.email || '').toLowerCase();

  resellers = allResellers.filter((r) => {
    const resellerName = (r.reseller_name || r.name || '').toLowerCase();
    const resellerEmail = (r.email || r.primary_email || '').toLowerCase();

    return (
      String(r.id) === String(currentUser?.reseller_id) ||
      String(r.user_id) === String(currentUser?.id) ||
      (currentResellerName && resellerName === currentResellerName.toLowerCase()) ||
      (currentEmail && resellerEmail === currentEmail)
    );
  });
} else {
        console.warn('Unhandled user role in Reports:', userRole);
        resellers = [];
      }

      console.log('Fetched resellers:', resellers);

      if (!Array.isArray(resellers)) {
        resellers = resellers ? [resellers] : [];
      }

      const normalized = resellers.map((r) => ({
        ...r,
        _normalized_status: normalizeStatus(
          r.status || r.state || r.account_status
        ),
        reseller_since: r.reseller_since || r.created_at || r.createdAt || '',
      }));

      setReportData(normalized);
      setFilteredData(normalized);
    } catch (err) {
      console.error('Error fetching resellers:', err);
      toast.error(displayText('Failed to load resellers report.'));
      setReportData([]);
      setFilteredData([]);
    } finally {
      setLoading(false);
    }
  };

  fetchResellers();
}, [activeSubTab, userRole, loggedInResellerId]);

  useEffect(() => {
    let filtered = [...reportData];

    // Status filter
    if (statusFilter !== 'all') {
      const isActive = statusFilter === 'active';
      filtered = filtered.filter((item) => {
        const st = item._normalized_status || normalizeStatus(item.status);
        return isActive ? st === 'active' : st === 'inactive';
      });
    }

    // Date-based filter
    if (activeSubTab === 'usage') {
      const end = parseToDate(endDate);

      if (end) {
        filtered = filtered.filter((item) => {
          const createdRaw =
            item.reseller_since || item.created_at || item.createdAt || item.created;
          const createdDate = parseToDate(createdRaw);
          if (!createdDate) return true;
          return createdDate.getTime() <= end.getTime();
        });
      }
    } else {
      if (monthFilter !== 'all' && yearFilter !== 'all') {
        const filterMonth = parseInt(monthFilter, 10);
        const filterYear = parseInt(yearFilter, 10);

        filtered = filtered.filter((item) => {
          const createdRaw =
            item.reseller_since || item.created_at || item.createdAt || item.created;
          const createdDate = parseToDate(createdRaw);
          if (!createdDate) return false;

          const createdMonth = createdDate.getMonth() + 1;
          const createdYearVal = createdDate.getFullYear();

          if (createdYearVal < filterYear) return true;
          if (createdYearVal > filterYear) return false;
          return createdMonth <= filterMonth;
        });
      }
    }

    setFilteredData(filtered);
    setCurrentPage(1);
    setSelectedIds(new Set());
    setSelectAll(false);
  }, [
    statusFilter,
    monthFilter,
    yearFilter,
    reportData,
    activeSubTab,
    startDate,
    endDate,
  ]);

  const handleSort = (field) => {
    setSortField(field);
    setSortOrder((prev) =>
      sortField === field && prev === 'asc' ? 'desc' : 'asc'
    );
  };

  const sorted = [...filteredData].sort((a, b) => {
    let aVal = a[sortField] ?? '';
    let bVal = b[sortField] ?? '';

    if (sortField === 'client_name' || sortField === 'reseller_name') {
      aVal = a.reseller_name ?? a.client_name ?? '';
      bVal = b.reseller_name ?? b.client_name ?? '';
    }

    if (typeof aVal === 'string') aVal = aVal.toLowerCase();
    if (typeof bVal === 'string') bVal = bVal.toLowerCase();

    if (aVal === bVal) return 0;
    return sortOrder === 'asc' ? (aVal < bVal ? -1 : 1) : (aVal > bVal ? -1 : 1);
  });

  const paginated = sorted.slice((currentPage - 1) * perPage, currentPage * perPage);

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const newSelected = new Set(prev);
      if (newSelected.has(id)) newSelected.delete(id);
      else newSelected.add(id);
      return newSelected;
    });
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedIds(new Set());
      setSelectAll(false);
    } else {
      setSelectedIds(new Set(paginated.map((item) => item.id)));
      setSelectAll(true);
    }
  };

  useEffect(() => {
    setSelectAll(
      paginated.length > 0 && paginated.every((item) => selectedIds.has(item.id))
    );
  }, [currentPage, paginated, selectedIds]);

  const renderPrimaryContact = (row) => {
    const name = row.primary_name || row.contact_name || row.reseller_name || '';
    const email = row.primary_email || row.email || row.primary_contact || '';
    if (!name && !email) return null;
    return (
      <div style={{ lineHeight: '1.35', fontFamily, fontSize: `${bodyFontSize}px` }}>
        {name && (
          <div style={{ fontWeight: '500', fontFamily }}>
            {name}
          </div>
        )}
        {email && (
          <div
            style={{
              fontSize: `${Math.max(10, bodyFontSize - 2)}px`,
              color: theme.colors.textSecondary || '#666',
              fontFamily,
            }}
          >
            {email}
          </div>
        )}
      </div>
    );
  };

  const displayStatus = (row) => {
    const st = row._normalized_status || normalizeStatus(row.status);
    return st === 'active' ? 'Active' : 'Deactivated';
  };

  const filterClientAdminsForReseller = (list = []) => {
    return list.filter((c) => {
      const role = (c.created_by_role || c.creator_role || '').toString().toLowerCase();
      const createdBy = (c.created_by || c.owner_type || '').toString().toLowerCase();

      if (!role && !createdBy) return true;
      return role === 'reseller' || createdBy === 'reseller';
    });
  };

  const handleResellerClick = async (reseller) => {
    try {
      const clientResp = await getClientAdminsByReseller(reseller.id);
      let clientAdmins = clientResp.data?.data || clientResp.data || [];
      clientAdmins = filterClientAdminsForReseller(clientAdmins);

      const payload = [
        {
          reseller,
          clientAdmins,
        },
      ];

      const filters = {
        activeSubTab,
        statusFilter,
        startDate,
        endDate,
        monthFilter,
        yearFilter,
      };

      const targetPath =
        activeSubTab === 'usage'
          ? '/usage-report'
          : activeSubTab === 'commission'
          ? '/commission-report'
          : '/userreport';

      const selectedResellerIds = [
        String(reseller.id ?? reseller.reseller_id ?? reseller._id ?? ''),
      ];

      navigate(targetPath, {
        state: {
          resellerClients: payload,
          fromResellers: true,
          filters,
          selectedResellerIds,
        },
      });
    } catch (err) {
      console.error('Failed to fetch clients for reseller:', err);
      toast.error(displayText('Could not load client data for this reseller.'));
    }
  };

  const buildPayloadAndNavigate = async (
    resellerList = [],
    clientAdminsByResellerMap = {},
    targetPath = '/userreport',
    selectedResellerIds = null
  ) => {
    const resellerClients = resellerList.map((r) => {
      const id = r.id ?? r.reseller_id ?? r._id ?? null;
      const clients = clientAdminsByResellerMap[String(id)] || [];
      return { reseller: r, clientAdmins: clients };
    });

    const unmatched = [];
    Object.keys(clientAdminsByResellerMap).forEach((key) => {
      const found = resellerList.some((r) => {
        const rid = r.id ?? r.reseller_id ?? r._id ?? null;
        return String(rid) === String(key);
      });
      if (!found) unmatched.push(...clientAdminsByResellerMap[key]);
    });

    if (unmatched.length > 0) {
      resellerClients.push({
        reseller: { id: 'unknown', reseller_name: 'Unknown reseller(s)' },
        clientAdmins: unmatched,
      });
    }

    const filters = {
      activeSubTab,
      statusFilter,
      startDate,
      endDate,
      monthFilter,
      yearFilter,
    };

    navigate(targetPath, {
      state: {
        resellerClients,
        fromResellers: true,
        filters,
        ...(selectedResellerIds && selectedResellerIds.length
          ? { selectedResellerIds: selectedResellerIds.map(String) }
          : {}),
      },
    });
  };

  const handleViewReport = async () => {
    const targetPath =
      activeSubTab === 'usage'
        ? '/usage-report'
        : activeSubTab === 'commission'
        ? '/commission-report'
        : '/userreport';

    if (selectedIds.size === 0 || selectAll) {
      try {
        const clientResp = await api.get('/client-admins');
        let allClientAdmins = clientResp.data?.data || clientResp.data || [];
        allClientAdmins = filterClientAdminsForReseller(allClientAdmins);

        let resellers = filteredData;

        if (!resellers || resellers.length === 0) {
  if (userRole === 'superadmin' || userRole === 'admin') {
    const rresp = await api.get('/resellers');
    resellers = rresp.data?.data || rresp.data || [];
  } else if (userRole === 'reseller' && loggedInResellerId) {
  const rresp = await api.get('/resellers');
  const allResellers = rresp.data?.data || rresp.data || [];

  resellers = allResellers.filter((r) => {
    const resellerId =
      r.id ?? r.reseller_id ?? r.user_id ?? r.owner_id ?? null;

    return String(resellerId) === String(loggedInResellerId);
  });
} else {
    resellers = [];
  }
}

        const clientsByReseller = {};
        allClientAdmins.forEach((c) => {
          const key =
            c.reseller_id ??
            c.reseller?.id ??
            c.resellerId ??
            c.parent_reseller_id ??
            'unknown';
          const k = String(key ?? 'unknown');
          if (!clientsByReseller[k]) clientsByReseller[k] = [];
          clientsByReseller[k].push(c);
        });

        await buildPayloadAndNavigate(resellers, clientsByReseller, targetPath);
      } catch (err) {
        console.error('Failed to fetch all client admins/resellers', err);
        toast.error('Failed to fetch data for the report.');
      }
      return;
    }

    const resellerIds = Array.from(selectedIds);
    const clientAdminsByReseller = {};
    const resellerList = [];

    try {
      for (const id of resellerIds) {
        try {
          const res = await getClientAdminsByReseller(id);
          let data = res.data?.data || res.data || [];
          data = filterClientAdminsForReseller(data);
          clientAdminsByReseller[String(id)] = data;
        } catch (err) {
          console.warn(`${"Failed to fetch clients for reseller "}${id}${""}`, err);
          clientAdminsByReseller[String(id)] = [];
        }

        let resellerObj = reportData.find(
          (r) => String(r.id) === String(id) || String(r.reseller_id) === String(id)
        );
        if (!resellerObj) {
          try {
            const rres = await api.get(`/resellers/${id}`);
            resellerObj = rres.data?.data || rres.data || { id };
          } catch (err) {
            resellerObj = { id };
          }
        }
        resellerList.push(resellerObj);
      }

      await buildPayloadAndNavigate(
        resellerList,
        clientAdminsByReseller,
        targetPath,
        resellerIds
      );
    } catch (err) {
      console.error('Error building reseller-client payload', err);
      toast.error(displayText('Error fetching client admins for selected resellers.'));
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const handleExport = () => {
    try {
      if (!sorted || sorted.length === 0) {
        toast.info('No data to export.');
        return;
      }

      const header = [
        'ID',
        'Reseller Name',
        'Location',
        'City',
        'State',
        'Primary Name',
        'Primary Email',
        'Status',
      ];

      const rows = sorted.map((row) => {
        const resellerName = row.reseller_name || row.name || 'Unknown Reseller';
        const primaryName =
          row.primary_name || row.contact_name || row.reseller_name || '';
        const primaryEmail =
          row.primary_email || row.email || row.primary_contact || '';
        const status = displayStatus(row);

        return [
          row.id ?? '',
          resellerName,
          row.location || '',
          row.city || '',
          row.state || '',
          primaryName,
          primaryEmail,
          status,
        ];
      });

      const escapeCsv = (val) => {
        if (val === null || val === undefined) return '';
        const str = String(val).replace(/"/g, '""');
        if (/[",\n]/.test(str)) return `"${str}"`;
        return str;
      };

      const csvContent = [header, ...rows]
        .map((row) => row.map(escapeCsv).join(','))
        .join('\n');

      const blob = new Blob([csvContent], {
        type: 'text/csv;charset=utf-8;',
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      const base =
        activeSubTab === 'usage'
          ? 'usage-report'
          : activeSubTab === 'commission'
          ? 'commission-report'
          : 'user-report';

      link.href = url;
      link.download = `${base}-${Date.now()}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error exporting CSV', err);
      toast.error('Failed to export data.');
    }
  };

  const handleViewFinancialReport = () => {
    const targetPath = '/financial-report';

    const filters = {
      activeSubTab,
      statusFilter,
      startDate,
      endDate,
      monthFilter,
      yearFilter,
    };

    navigate(targetPath, {
      state: {
        fromResellers: true,
        filters,
      },
    });
  };

  const selectStyle = {
    padding: '8px 36px 8px 14px',
    borderRadius: '999px',
    border: '1px solid #d0d7de',
    backgroundColor: theme.colors.bgPrimary,
    color: theme.colors.textPrimary,
    fontSize: `${Math.max(10, bodyFontSize - 2)}px`,
    fontWeight: '500',
    height: '36px',
    minWidth: '120px',
    cursor: 'pointer',
    outline: 'none',
    appearance: 'none',
    WebkitAppearance: 'none',
    MozAppearance: 'none',
    boxShadow: '0 1px 2px rgba(0,0,0,0.08)',
    backgroundImage: `
      linear-gradient(45deg, transparent 50%, #555 50%),
      linear-gradient(135deg, #555 50%, transparent 50%)
    `,
    backgroundPosition: 'calc(100% - 18px) 14px, calc(100% - 12px) 14px',
    backgroundSize: '6px 6px, 6px 6px',
    backgroundRepeat: 'no-repeat',
    fontFamily,
  };

  const actionButtonStyle = {
    background: theme.gradients?.secondary || theme.colors.secondary,
    color: theme.colors.textWhite,
    padding: 'clamp(6px, 2vw, 8px) clamp(10px, 2vw, 12px)',
    borderRadius: '8px',
    border: 'none',
    fontWeight: 'bold',
    fontSize: `${Math.max(10, bodyFontSize - 2)}px`,
    cursor: 'pointer',
    minWidth: '120px',
    height: '34px',
    fontFamily,
  };

  const topContainerStyle = {
    background: theme.gradients?.primary || theme.colors.primary,
    padding: '0.8rem 1.2rem',
    borderRadius: '12px',
    marginBottom: '1rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: '0.8rem',
    color: theme.colors.textWhite,
    fontFamily,
  };

  const renderStatusFilter = () => (
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontFamily }}>
      <span
        style={{
          fontSize: `${Math.max(10, bodyFontSize - 2)}px`,
          fontWeight: '500',
          fontFamily,
        }}
      >
        Status:
      </span>
      <select
        value={statusFilter}
        onChange={(e) => setStatusFilter(e.target.value)}
        style={selectStyle}
      >
        <option value="all">All</option>
        <option value="active">Active</option>
        <option value="inactive">Inactive</option>
      </select>
    </div>
  );

  return (
    <div style={{ fontFamily, fontSize: `${bodyFontSize}px` }}>
      {/* WHITE CARD CONTAINER - Same as other pages */}
      <div
        style={{
          backgroundColor: "#ffffff",
          padding: "1rem",
          borderRadius: "10px",
          boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
          fontFamily,
          marginTop: "10px",
        }}
      >
        {/* Title */}
        <h4
          style={{
            fontSize: `${titleFontSize}px`,
            fontWeight: 'bold',
            color: theme.colors.textPrimary,
            marginBottom: '1rem',
            textAlign: 'left',
            fontFamily,
          }}
        >
          {currentReport.title}
        </h4>

        {/* Conditional Filters & Actions */}
        {activeSubTab === 'commission' ? (
          <>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '0.8rem',
                marginBottom: '1rem',
                fontFamily,
              }}
            >
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                }}
              >
                {renderStatusFilter()}
              </div>

              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: '0.8rem',
                  fontFamily,
                }}
              >
                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                  <button type="button" onClick={handlePrint} style={actionButtonStyle}>
                    Print
                  </button>
                  <button type="button" onClick={handleExport} style={actionButtonStyle}>
                    Export
                  </button>
                </div>

                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.7rem',
                    flexWrap: 'wrap',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      fontFamily,
                    }}
                  >
                    <span
                      style={{
                        fontSize: `${Math.max(10, bodyFontSize - 2)}px`,
                        fontWeight: '500',
                        fontFamily,
                      }}
                    >
                      Month:
                    </span>
                    <select
                      value={monthFilter}
                      onChange={(e) => setMonthFilter(e.target.value)}
                      style={selectStyle}
                    >
                      <option value="all">All</option>
                      {months.map((m, i) => (
                        <option key={i + 1} value={String(i + 1)}>
                          {m}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      fontFamily,
                    }}
                  >
                    <span
                      style={{
                        fontSize: `${Math.max(10, bodyFontSize - 2)}px`,
                        fontWeight: '500',
                        fontFamily,
                      }}
                    >
                      Year:
                    </span>
                    <select
                      value={yearFilter}
                      onChange={(e) => setYearFilter(e.target.value)}
                      style={selectStyle}
                    >
                      <option value="all">All</option>
                      {years.map((y) => (
                        <option key={y} value={String(y)}>
                          {y}
                        </option>
                      ))}
                    </select>
                  </div>

                  <button onClick={handleViewReport} style={actionButtonStyle}>
                    View Report
                  </button>
                </div>
              </div>
            </div>
          </>
        ) : activeSubTab === 'usage' ? (
          <>
            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
                marginBottom: '0.6rem',
                fontFamily,
              }}
            >
              {renderStatusFilter()}
            </div>

            <div style={topContainerStyle}>
              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                <button type="button" onClick={handlePrint} style={actionButtonStyle}>
                  Print
                </button>
                <button type="button" onClick={handleExport} style={actionButtonStyle}>
                  Export
                </button>
              </div>

              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.7rem',
                  flexWrap: 'wrap',
                  justifyContent: 'flex-end',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    fontFamily,
                  }}
                >
                  <span
                    style={{
                      fontSize: `${Math.max(10, bodyFontSize - 2)}px`,
                      fontWeight: '500',
                      fontFamily,
                    }}
                  >
                    Start Date:
                  </span>
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    style={{
                      padding: '8px 12px',
                      borderRadius: '999px',
                      border: '1px solid #d0d7de',
                      fontSize: `${Math.max(10, bodyFontSize - 2)}px`,
                      fontWeight: '500',
                      height: '36px',
                      minWidth: '150px',
                      outline: 'none',
                      boxShadow: '0 1px 2px rgba(0,0,0,0.08)',
                      fontFamily,
                    }}
                  />
                </div>

                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    fontFamily,
                  }}
                >
                  <span
                    style={{
                      fontSize: `${Math.max(10, bodyFontSize - 2)}px`,
                      fontWeight: '500',
                      fontFamily,
                    }}
                  >
                    End Date:
                  </span>
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    style={{
                      padding: '8px 12px',
                      borderRadius: '999px',
                      border: '1px solid #d0d7de',
                      fontSize: `${Math.max(10, bodyFontSize - 2)}px`,
                      fontWeight: '500',
                      height: '36px',
                      minWidth: '150px',
                      outline: 'none',
                      boxShadow: '0 1px 2px rgba(0,0,0,0.08)',
                      fontFamily,
                    }}
                  />
                </div>

                <button onClick={handleViewReport} style={actionButtonStyle}>
                  View Report
                </button>

                <button onClick={handleViewFinancialReport} style={actionButtonStyle}>
                  View Financial Report
                </button>
              </div>
            </div>
          </>
        ) : (
          <div style={topContainerStyle}>
            <div
              style={{
                display: 'flex',
                gap: '1rem',
                flexWrap: 'wrap',
                alignItems: 'center',
                fontFamily,
              }}
            >
              {renderStatusFilter()}

              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  fontFamily,
                }}
              >
                <span
                  style={{
                    fontSize: `${Math.max(10, bodyFontSize - 2)}px`,
                    fontWeight: '500',
                    fontFamily,
                  }}
                >
                  Month:
                </span>
                <select
                  value={monthFilter}
                  onChange={(e) => setMonthFilter(e.target.value)}
                  style={selectStyle}
                >
                  <option value="all">All</option>
                  {months.map((m, i) => (
                    <option key={i + 1} value={String(i + 1)}>
                      {m}
                    </option>
                  ))}
                </select>
              </div>

              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  fontFamily,
                }}
              >
                <span
                  style={{
                    fontSize: `${Math.max(10, bodyFontSize - 2)}px`,
                    fontWeight: '500',
                    fontFamily,
                  }}
                >
                  Year:
                </span>
                <select
                  value={yearFilter}
                  onChange={(e) => setYearFilter(e.target.value)}
                  style={selectStyle}
                >
                  <option value="all">All</option>
                  {years.map((y) => (
                    <option key={y} value={String(y)}>
                      {y}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <button onClick={handleViewReport} style={actionButtonStyle}>
              View Report
            </button>
          </div>
        )}

        {/* TOP PAGINATION */}
        <Pagination
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          perPage={perPage}
          setPerPage={setPerPage}
          totalItems={sorted.length}
          gotoPage={gotoPage}
          setGotoPage={setGotoPage}
        />

        {/* TABLE CONTAINER */}
        <div
          style={{
            overflowX: 'auto',
            backgroundColor: "#ffffff",
            borderRadius: "10px",
            border: "1px solid #dee2e6",
            marginTop: "1rem",
            marginBottom: "1rem",
          }}
        >
          {loading ? (
            <div
              style={{
                padding: '3rem',
                textAlign: 'center',
                color: theme.colors.textSecondary || '#888',
                fontFamily,
                fontSize: `${bodyFontSize}px`,
              }}
            >{displayText(" Loading resellers... ")}</div>
          ) : (
            <table
              style={{
                width: '100%',
                borderCollapse: 'collapse',
                fontFamily,
                fontSize: `${bodyFontSize}px`,
              }}
            >
              <thead>
                <tr
                  style={{
                    backgroundColor: theme.colors.primary,
                    color: theme.colors.textWhite,
                    position: 'sticky',
                    top: 0,
                    zIndex: 10,
                    fontFamily,
                  }}
                >
                  <th style={{ padding: '0.5rem', width: '40px' }}>
                    <label
                      style={{
                        cursor: 'pointer',
                        display: 'flex',
                        justifyContent: 'center',
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={selectAll}
                        onChange={handleSelectAll}
                        style={{
                          appearance: 'none',
                          width: '12px',
                          height: '12px',
                          border: '2px solid rgba(255,255,255,0.9)',
                          borderRadius: '50%',
                          backgroundColor: selectAll ? '#ffffff' : 'transparent',
                          boxSizing: 'border-box',
                          cursor: 'pointer',
                        }}
                      />
                    </label>
                  </th>

                  {[
                    'id',
                    'client_name',
                    'location',
                    'city',
                    'state',
                    'primary_contact',
                    'status',
                  ].map((field) => (
                    <th
                      key={field}
                      onClick={() =>
                        handleSort(field === 'client_name' ? 'reseller_name' : field)
                      }
                      style={{
                        padding: '0.5rem',
                        textAlign: 'left',
                        fontSize: `${bodyFontSize}px`,
                        cursor: 'pointer',
                        userSelect: 'none',
                        fontFamily,
                      }}
                    >
                      <span
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '6px',
                        }}
                      >
                        {field
                          .replace(/_/g, ' ')
                          .replace(/\b\w/g, (l) => l.toUpperCase())}
                        {sortField === (field === 'client_name' ? 'reseller_name' : field) && (
                          <i
                            className={`bi ${
                              sortOrder === 'asc' ? 'bi-arrow-up' : 'bi-arrow-down'
                            }`}
                            style={{ fontSize: `${Math.max(10, bodyFontSize - 2)}px` }}
                          ></i>
                        )}
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {paginated.length > 0 ? (
                  paginated.map((row) => (
                    <tr
                      key={row.id}
                      style={{
                        borderBottom: `1px solid #dee2e6`,
                        cursor: 'pointer',
                        backgroundColor: selectedIds.has(row.id)
                          ? '#e3f2fd'
                          : '#ffffff',
                        fontFamily,
                      }}
                      onClick={(e) => {
                        if (
                          !e.target.closest('input[type="checkbox"]') &&
                          !e.target.closest('span[style*="textDecoration"]')
                        ) {
                          toggleSelect(row.id);
                        }
                      }}
                      onMouseEnter={(e) =>
                        !selectedIds.has(row.id) &&
                        (e.currentTarget.style.backgroundColor = '#f8f9fa')
                      }
                      onMouseLeave={(e) =>
                        !selectedIds.has(row.id) &&
                        (e.currentTarget.style.backgroundColor = '#ffffff')
                      }
                    >
                      <td
                        style={{ padding: '0.5rem', textAlign: 'center' }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <input
                          type="checkbox"
                          checked={selectedIds.has(row.id)}
                          onChange={() => toggleSelect(row.id)}
                          style={{
                            appearance: 'none',
                            width: '12px',
                            height: '12px',
                            border: selectedIds.has(row.id)
                              ? 'none'
                              : '2px solid #999',
                            borderRadius: '50%',
                            backgroundColor: selectedIds.has(row.id)
                              ? theme.colors.primary
                              : 'transparent',
                            cursor: 'pointer',
                          }}
                        />
                      </td>

                      <td
                        style={{
                          padding: '0.5rem',
                          fontFamily,
                          fontSize: `${bodyFontSize}px`,
                        }}
                      >
                        {row.id}
                      </td>

                      <td
                        style={{
                          padding: '0.5rem',
                          fontWeight: '500',
                          fontFamily,
                          fontSize: `${bodyFontSize}px`,
                        }}
                      >
                        <span
                          onClick={(e) => {
                            e.stopPropagation();
                            handleResellerClick(row);
                          }}
                          style={{
                            color: theme.colors.primary,
                            textDecoration: 'underline',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            fontFamily,
                          }}
                        >
                          {row.reseller_name || row.name || displayText('Unknown Reseller')}
                        </span>
                      </td>

                      <td
                        style={{
                          padding: '0.5rem',
                          fontFamily,
                          fontSize: `${bodyFontSize}px`,
                        }}
                      >
                        {row.location || ''}
                      </td>
                      <td
                        style={{
                          padding: '0.5rem',
                          fontFamily,
                          fontSize: `${bodyFontSize}px`,
                        }}
                      >
                        {row.city || ''}
                      </td>
                      <td
                        style={{
                          padding: '0.5rem',
                          fontFamily,
                          fontSize: `${bodyFontSize}px`,
                        }}
                      >
                        {row.state || ''}
                      </td>
                      <td
                        style={{
                          padding: '0.5rem',
                          fontFamily,
                          fontSize: `${bodyFontSize}px`,
                        }}
                      >
                        {renderPrimaryContact(row)}
                      </td>
                      <td
                        style={{
                          padding: '0.5rem',
                          color:
                            row._normalized_status === 'active'
                              ? theme.colors.success
                              : theme.colors.danger,
                          fontWeight: 'bold',
                          fontFamily,
                          fontSize: `${bodyFontSize}px`,
                        }}
                      >
                        {displayStatus(row)}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan={8}
                      style={{
                        padding: '3rem',
                        textAlign: 'center',
                        color: theme.colors.textSecondary || '#999',
                        fontFamily,
                        fontSize: `${bodyFontSize}px`,
                      }}
                    >
                      No records to show
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>

        {/* BOTTOM PAGINATION */}
        <Pagination
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          perPage={perPage}
          setPerPage={setPerPage}
          totalItems={sorted.length}
          gotoPage={gotoPage}
          setGotoPage={setGotoPage}
        />
      </div>
    </div>
  );
};

export default Reports;